// The left nav's grouping, filtering and per-person preferences (2026-10-09 decision #9).
//
// Groups: Clients / Products / Platforms / Just me. Jabberr does not know Yard's line of business, so a project's
// group is the one its owner set (navGroup, on the project page), else a default from what Jabberr does know:
// My space and private projects are Just me, anything else is a product until its owner says otherwise. A project
// the caller is a guest in is always a client's, whatever the owner chose for the team.
//
// Pinned projects and folded groups are per-person preferences on the server (lib/preferences.ts).
//
// A Parking lot idea project (isIdea, OPEN-38) is in no group and never under Pinned (John 2026-10-10): it is opened
// from its card, and its conversations still show in Recent. Once Loop approval makes it a team project it is no
// longer an idea and shows in its group like any other.

export const NAV_GROUPS = ['clients', 'products', 'platforms', 'just_me'] as const;
export type NavGroup = (typeof NAV_GROUPS)[number];

export const NAV_GROUP_LABELS: Record<NavGroup, string> = {
  clients: 'Clients',
  products: 'Products',
  platforms: 'Platforms',
  just_me: 'Just me',
};

export interface NavProject {
  id: string;
  name: string;
  kind: string;
  role?: string;
  navGroup?: string | null;
  isIdea?: boolean;
}

/** Whether a project has a row in the nav's groups and Pinned: every project except an unapproved idea project. */
export function inNav(p: NavProject): boolean {
  return p.isIdea !== true;
}

export function navGroupOf(p: NavProject): NavGroup {
  if (p.kind === 'personal') return 'just_me';
  if (p.role === 'guest') return 'clients';
  if (p.navGroup && (NAV_GROUPS as readonly string[]).includes(p.navGroup)) return p.navGroup as NavGroup;
  if (p.kind === 'private') return 'just_me';
  return 'products';
}

/** Projects by group, each group in name order with My space first in Just me; empty groups are left out. */
export function groupProjects<P extends NavProject>(projects: P[]): Array<{ group: NavGroup; projects: P[] }> {
  const out: Array<{ group: NavGroup; projects: P[] }> = [];
  for (const group of NAV_GROUPS) {
    const members = projects
      .filter((p) => inNav(p) && navGroupOf(p) === group)
      .sort((a, b) => (a.kind === 'personal' ? -1 : b.kind === 'personal' ? 1 : a.name.localeCompare(b.name)));
    if (members.length > 0) out.push({ group, projects: members });
  }
  return out;
}

/** Case-insensitive match on any of the words the person typed. Empty matches everything. */
export function matchesQuery(query: string, ...fields: Array<string | null | undefined>): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const hay = fields.filter(Boolean).join(' ').toLowerCase();
  return q.split(/\s+/).every((word) => hay.includes(word));
}

/** A short code for a project chip: the initials of its first two words, or its first two letters. */
export function projectCode(name: string): string {
  const words = name.replace(/['’]s\b/g, '').split(/[\s\-_]+/).filter(Boolean);
  if (words.length >= 2) return (words[0]![0]! + words[1]![0]!).toUpperCase();
  return (words[0] ?? name).slice(0, 2).toUpperCase();
}
